import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, DollarSign, MapPin, Briefcase, ArrowUp, ArrowDown, Globe, Award, Brain, Laptop, Building, Clock, LineChart, Star, BarChart as ChartBar, Info, Zap } from 'lucide-react';
import Button from '../components/ui/Button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';

// Simulated data for market trends
const trendData = {
  growingIndustries: [
    { name: 'Technology', growth: 22, jobs: 150000 },
    { name: 'Healthcare', growth: 18, jobs: 120000 },
    { name: 'Green Energy', growth: 15, jobs: 80000 },
    { name: 'Logistics', growth: 12, jobs: 65000 },
    { name: 'E-commerce', growth: 10, jobs: 55000 }
  ],
  inDemandSkills: [
    { name: 'AI/ML', demand: 95, growth: 25 },
    { name: 'Cloud Computing', demand: 90, growth: 20 },
    { name: 'Data Analysis', demand: 88, growth: 18 },
    { name: 'Cybersecurity', demand: 85, growth: 22 },
    { name: 'DevOps', demand: 82, growth: 15 }
  ],
  salaryTrends: [
    { role: 'Data Scientist', salary: 120000, growth: 12 },
    { role: 'Software Engineer', salary: 110000, growth: 10 },
    { role: 'Product Manager', salary: 115000, growth: 8 },
    { role: 'Cloud Architect', salary: 130000, growth: 15 },
    { role: 'AI Engineer', salary: 125000, growth: 18 }
  ],
  locationDemand: [
    { city: 'San Francisco', openings: 24000, growth: 15 },
    { city: 'New York', openings: 22000, growth: 12 },
    { city: 'Seattle', openings: 18000, growth: 14 },
    { city: 'Austin', openings: 15000, growth: 20 },
    { city: 'Boston', openings: 12000, growth: 10 }
  ],
  workModeDistribution: {
    remote: 35,
    hybrid: 45,
    onsite: 20
  },
  emergingRoles: [
    { title: 'AI Ethics Officer', demand: 'High', trend: 'Growing' },
    { title: 'Cloud Security Architect', demand: 'Very High', trend: 'Growing' },
    { title: 'Data Privacy Manager', demand: 'High', trend: 'Growing' },
    { title: 'Sustainability Consultant', demand: 'Medium', trend: 'Growing' },
    { title: 'Digital Transformation Lead', demand: 'High', trend: 'Growing' }
  ],
  skillPremiums: [
    { skill: 'AI/ML', premium: 25000 },
    { skill: 'Cloud Architecture', premium: 20000 },
    { skill: 'Cybersecurity', premium: 18000 },
    { skill: 'Data Science', premium: 15000 },
    { skill: 'DevOps', premium: 12000 }
  ],
  decliningIndustries: [
    { name: 'Traditional Retail', decline: -8, impact: 'High' },
    { name: 'Print Media', decline: -12, impact: 'High' },
    { name: 'Coal Energy', decline: -15, impact: 'Medium' },
    { name: 'Traditional Banking', decline: -5, impact: 'Medium' },
    { name: 'Manual Manufacturing', decline: -10, impact: 'High' }
  ]
};

const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];

const MarketTrends: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'6m' | '1y' | '2y'>('1y');

  const timeframeData = {
    '6m': {
      overallGrowth: '+8.3%',
      remoteJobs: '40%',
      avgSalary: '+5.2%',
      jobOpenings: '280K+'
    },
    '1y': {
      overallGrowth: '+15.7%',
      remoteJobs: '35%',
      avgSalary: '+8.3%',
      jobOpenings: '450K+'
    },
    '2y': {
      overallGrowth: '+25.4%',
      remoteJobs: '30%',
      avgSalary: '+12.5%',
      jobOpenings: '650K+'
    }
  };

  const currentData = timeframeData[selectedTimeframe];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  // Format data for area chart
  const growthData = trendData.growingIndustries.map(industry => ({
    name: industry.name,
    growth: industry.growth,
    jobs: industry.jobs / 1000 // Convert to thousands for better display
  }));

  // Format data for pie chart
  const workModeData = Object.entries(trendData.workModeDistribution).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <div className="relative bg-gradient-to-b from-primary-900 to-primary-800 py-16 mb-12 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-700 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
                  Market Trends & Insights
                </h1>
                <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
                  Stay informed about the latest career market trends and make data-driven decisions.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    <span>Industry Growth</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <DollarSign className="h-5 w-5 mr-2" />
                    <span>Salary Insights</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Globe className="h-5 w-5 mr-2" />
                    <span>Global Demand</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Overview Cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Overall Growth</h3>
                <TrendingUp className="text-green-500 h-6 w-6" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{currentData.overallGrowth}</p>
              <p className="text-sm text-gray-600">Year over year increase</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Remote Jobs</h3>
                <Laptop className="text-blue-500 h-6 w-6" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{currentData.remoteJobs}</p>
              <p className="text-sm text-gray-600">Of total positions</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Avg. Salary</h3>
                <DollarSign className="text-green-500 h-6 w-6" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{currentData.avgSalary}</p>
              <p className="text-sm text-gray-600">Salary growth rate</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Job Openings</h3>
                <Briefcase className="text-purple-500 h-6 w-6" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{currentData.jobOpenings}</p>
              <p className="text-sm text-gray-600">Active positions</p>
            </div>
          </motion.div>

          {/* Industry Growth Chart */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <TrendingUp className="h-6 w-6 text-green-500 mr-2" />
                Industry Growth Trends
              </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growthData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="growth" 
                      stroke="#3B82F6" 
                      fill="#3B82F6" 
                      fillOpacity={0.2} 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Users className="h-6 w-6 text-blue-500 mr-2" />
                Work Mode Distribution
              </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={workModeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {workModeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>

          {/* Salary Trends */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <DollarSign className="h-6 w-6 text-green-500 mr-2" />
                Salary Trends
              </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData.salaryTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="role" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="salary" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Brain className="h-6 w-6 text-purple-500 mr-2" />
                Skill Premiums
              </h2>
              <div className="space-y-4">
                {trendData.skillPremiums.map((skill, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900">{skill.skill}</span>
                    <div className="flex items-center">
                      <span className="text-green-600 font-semibold">
                        +${skill.premium.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Emerging Roles */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-md p-6 mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Zap className="h-6 w-6 text-yellow-500 mr-2" />
              Emerging Roles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendData.emergingRoles.map((role, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{role.title}</h3>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      role.demand === 'Very High' 
                        ? 'bg-green-100 text-green-800'
                        : role.demand === 'High'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {role.demand} Demand
                    </span>
                    <span className="text-gray-600 text-sm">{role.trend}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Location Demand */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <MapPin className="h-6 w-6 text-red-500 mr-2" />
              Location Demand
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendData.locationDemand.map((location, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{location.city}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{location.openings.toLocaleString()} openings</span>
                    <span className="text-green-600">+{location.growth}%</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default MarketTrends;